import { Box, Button, Card, CardBody, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import MyDiv from './meetingsManagement.style';
import { MeetingsManagementList, CreateMeetingModal } from '../components';
import { useEffect, useState } from 'react';
import { CustomToast } from '../../../components';
import { ILazyParams, useFetch } from '../../../hooks/useFetch';
import { useAuth } from '../../../hooks/useAuth';
import { useDisclosure } from '@chakra-ui/react';
import { CalendarIcon, TimeIcon, CheckCircleIcon, RepeatClockIcon, InfoIcon } from '@chakra-ui/icons';
import { IFilterOptions } from '../../../components/FilterCard/filterCard';

const cardConfig: Record<string, { color: string; bg: string; icon: any }> = {
    "Total Meetings": { color: "blue.600", bg: "blue.50", icon: CalendarIcon },
    "Today's Meetings": { color: "purple.600", bg: "purple.50", icon: TimeIcon },
    "Running": { color: "green.600", bg: "green.50", icon: RepeatClockIcon },
    "Upcoming": { color: "orange.600", bg: "orange.50", icon: InfoIcon },
    "Completed": { color: "gray.600", bg: "gray.50", icon: CheckCircleIcon },
};

const MeetingsManagement = () => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const { hasPermission } = useAuth();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [meetingData, setMeetingData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [summary, setSummary] = useState<any[]>([]);
    const [activeCard, setActiveCard] = useState<string | null>(null);

    const participantsModal = useDisclosure();
    const [participants, setParticipants] = useState<any[]>([]);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [meetingStatus, setMeetingStatus] = useState<string>("");

    const [cancelConfirm, setCancelConfirm] = useState<{ meetingId: number | null; isOpen: boolean }>({ meetingId: null, isOpen: false });
    const [syncAttendanceConfirm, setSyncAttendanceConfirm] = useState<{ meetingId: number | null; isOpen: boolean }>({ meetingId: null, isOpen: false });
    const [attendanceData, setAttendanceData] = useState<any[]>([]);
    const attendanceModal = useDisclosure();

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });


    const [query, setQuery] = useState<{ lazyParams: ILazyParams; filter: Record<string, any> }>({
        lazyParams: { first: 0, rows: 20, page: 0, sortField: '', sortOrder: 1 },
        filter: {},
    });

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(handler);
    }, [search]);

    const loadTableData = async (overrideQuery = query, overrideSearch = debouncedSearch) => {
        const _query = new URLSearchParams({
            lazyParams: JSON.stringify(overrideQuery.lazyParams),
            filter: JSON.stringify(overrideQuery.filter),
            search: overrideSearch
        }).toString();

        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;


        const response = await fetchApi(`Meetings/Meeting?${_query}&timeZoneId=${timeZoneId}`, "GET");
        if (response) {
            setMeetingData(response.data || response);
            setTotalCount(response.totalRecords || 0);
            setSummary(response.summary || []);
        }
    };

    const openParticipants = async (meetingId: number, status: string) => {
        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await fetchApi(`Meetings/Meeting/${meetingId}/participants?timeZoneId=${timeZoneId}`, "GET");
        if (response) {
            setParticipants(response);
            setSelectedMeetingId(meetingId);
            setMeetingStatus(status);
            participantsModal.onOpen();
        }
    };

    const confirmCancelMeeting = async () => {
        if (!cancelConfirm.meetingId) return;
        const response = await fetchApi(`Meetings/cancel-meeting/${cancelConfirm.meetingId}`, "PUT");
        if (response) {
            addToast({ message: "Meeting cancelled successfully", status: "success" });
            loadTableData();
        }
        setCancelConfirm({ meetingId: null, isOpen: false });
    };

    const confirmSyncAttendance = async () => {
        if (!syncAttendanceConfirm.meetingId) return;
        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await fetchApi(`Meetings/sync-meeting-attendance/${syncAttendanceConfirm.meetingId}?timeZoneId=${timeZoneId}`, "POST");
        if (response) {
            addToast({ message: "Attendance synced successfully", status: "success" });
            setAttendanceData(response.data || []);
            attendanceModal.onOpen();
        }
        setSyncAttendanceConfirm({ meetingId: null, isOpen: false });
    };

    const handleCardClick = (card: any) => {
        // ✅ Toggle off if same card clicked again
        if (activeCard === card.title) {
            setActiveCard(null);
            const newQuery = {
                ...query,
                filter: { ...query.filter },
                lazyParams: { ...query.lazyParams, page: 0, first: 0 }
            };
            delete newQuery.filter.Status;
            setQuery(newQuery);
            return;
        }

        setActiveCard(card.title);

        const statusMap: Record<string, string> = {
            "Today's Meetings": "Today",
            "Running": "Running",
            "Upcoming": "Not Yet Started",
            "Completed": "Completed",
        };

        const newStatus = statusMap[card.title];
        const newQuery = {
            ...query,
            filter: {
                ...query.filter,
                ...(newStatus ? { Status: newStatus } : {})
            },
            lazyParams: { ...query.lazyParams, page: 0, first: 0 }
        };

        if (!newStatus) delete newQuery.filter.Status;
        setQuery(newQuery);

    };

    const loadFilter = async () => {
        const response = await fetchApi("Filters/meetingFilter", "GET", null, null, '');
        if (response) {
            setFilter(response);
        }
    }

    useEffect(() => {
        loadTableData();
        loadFilter();
    }, [query, debouncedSearch]);

    return (
        <MyDiv>
            <Box className="page_wrapper">
                <Flex className="page_heading flex_header" justify="space-between" align="center" mb={4}>
                    <Text className="font-poppins text_semibold text_3xl">Meetings Management</Text>
                    <Flex gap={2} align="center">
                        {hasPermission("MeetingsManagement", "Create_Meeting") && (
                            <Button bg="#006838" color="#fff" _hover={{ bg: '#005529' }}
                                className="font-poppins text_sm text_md" onClick={onOpen}>
                                Create New Meeting
                            </Button>
                        )}
                    </Flex>
                </Flex>

                <Grid templateColumns={{ base: 'repeat(1,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(5,1fr)' }} gap={4} mb={4}>
                    {summary.map((card, index) => {
                        const config = cardConfig[card.title] || { color: "gray.600", bg: "gray.50", icon: InfoIcon };
                        const IconComp = config.icon;
                        const isActive = activeCard === card.title;
                        return (
                            <GridItem key={index}>
                                <Card className="card" cursor="pointer"
                                    border="2px solid"
                                    borderColor={isActive ? config.color : "transparent"}
                                    bg={isActive ? config.bg : "white"}
                                    boxShadow={isActive ? "md" : "sm"}
                                    _hover={{ boxShadow: "md", bg: config.bg }}
                                    transition="all 0.2s"
                                    onClick={() => handleCardClick(card)}
                                >
                                    <CardBody p={4}>
                                        <Flex direction="column" gap={2}>
                                            <Flex align="center" gap={2}>
                                                <Box bg={config.bg} p={2} borderRadius="md">
                                                    <IconComp color={config.color} boxSize={4} />
                                                </Box>
                                                <Text className="font-poppins text_xl text_medium">
                                                    {card.title}
                                                </Text>
                                            </Flex>
                                            <Text fontSize="3xl" fontWeight="700" color={config.color} className="font-poppins">
                                                {card.value}
                                            </Text>
                                        </Flex>
                                    </CardBody>
                                </Card>
                            </GridItem>
                        );
                    })}
                </Grid>

                {/* Table */}
                <Grid className="grid_container">
                    <GridItem colSpan={12}>
                        <MeetingsManagementList
                            meetingData={meetingData}
                            totalCount={totalCount}
                            reloadMeetings={loadTableData}
                            onOpenParticipants={openParticipants}
                            participants={participants}
                            participantsModal={participantsModal}
                            confirmCancelMeeting={confirmCancelMeeting}
                            setCancelConfirm={setCancelConfirm}
                            cancelConfirm={cancelConfirm}
                            confirmSyncAttendance={confirmSyncAttendance}
                            setSyncAttendanceConfirm={setSyncAttendanceConfirm}
                            syncAttendanceConfirm={syncAttendanceConfirm}
                            attendanceModal={attendanceModal}
                            attendanceData={attendanceData}
                            selectedMeetingId={selectedMeetingId}
                            setSelectedMeetingId={setSelectedMeetingId}
                            meetingStatus={meetingStatus}
                            query={query}
                            setQuery={setQuery}
                            setSearch={setSearch}
                            filter={filter}
                        />
                    </GridItem>
                </Grid>

                <CreateMeetingModal isOpen={isOpen} onClose={onClose} reloadMeetings={loadTableData} />
            </Box>
        </MyDiv>
    );
};

export default MeetingsManagement;